import { useState, useEffect, useRef } from "react";
import ScrollReveal from "./ScrollReveal";

const services = [
  {
    title: "META Ads",
    color: "from-blue-500 to-indigo-600",
    hoverColor: "from-blue-600 to-indigo-700",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
    ),
    text: "Targeted Facebook and Instagram advertising campaigns that drive real results and maximize your ROI.",
    features: ["Audience Targeting", "Creative Strategy", "A/B Testing", "Performance Analytics"],
    badge: "Core Service"
  },
  {
    title: "Google Paid Ads",
    color: "from-green-500 to-emerald-600",
    hoverColor: "from-green-600 to-emerald-700",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    ),
    text: "High-intent traffic campaigns that maximize your budget and drive qualified leads to your business.",
    features: ["PPC Management", "Keyword Research", "Landing Pages", "Conversion Tracking"],
    badge: "Core Service"
  },
  {
    title: "Software Development",
    color: "from-cyan-500 to-blue-600",
    hoverColor: "from-cyan-600 to-blue-700",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    ),
    text: "Custom software solutions built with cutting-edge technologies to streamline your business operations.",
    features: ["Web Applications", "API Development", "Cloud Solutions", "System Integration"],
    badge: "Core Service"
  },
  {
    title: "Mobile App Development",
    color: "from-purple-500 to-pink-600",
    hoverColor: "from-purple-600 to-pink-700",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
    ),
    text: "Native and cross-platform mobile applications that engage users and drive business growth.",
    features: ["iOS & Android", "UI/UX Design", "App Store Launch", "Maintenance"],
    badge: "Core Service"
  },
  {
    title: "AI Solutions",
    color: "from-orange-500 to-red-600",
    hoverColor: "from-orange-600 to-red-700",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    ),
    text: "Leverage cutting-edge AI and machine learning to automate processes and gain intelligent insights.",
    features: ["AI Chatbots", "ML Models", "Predictive Analytics", "NLP Solutions"],
    badge: "Core Service"
  },
];

const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeService, setActiveService] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className="relative bg-gradient-to-br from-gray-50 to-blue-50/30 py-24 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-4 h-4 bg-indigo-300 rounded-full animate-pulse"></div>
        <div className="absolute top-1/4 right-20 w-6 h-6 bg-purple-300 rounded-full animate-bounce"></div>
        <div className="absolute bottom-20 left-1/4 w-3 h-3 bg-green-300 rounded-full animate-ping"></div>
        <div className="absolute bottom-1/3 right-1/3 w-5 h-5 bg-rose-300 rounded-full animate-pulse"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header with Animation */}
        <div className={`text-center transition-all duration-1000 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-100 border border-indigo-200 mb-6">
            <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2 animate-pulse"></span>
            <span className="text-sm font-medium text-indigo-700 uppercase tracking-wider">
              Our Services
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent">
            Our Core Services
          </h2>
          <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mt-2">
            Ads, Software, Apps & AI
          </h2>
          <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Specializing in META Ads, Google Ads, custom software development, mobile applications, 
            and AI solutions to transform your business.
          </p>
        </div>

        {/* Services Grid */}
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <ScrollReveal
              key={index}
              direction={index % 2 === 0 ? "left" : "right"}
              delay={index * 100}
              duration={1000}
            >
              <div
                className="group relative"
                onMouseEnter={() => setActiveService(index)}
                onMouseLeave={() => setActiveService(null)}
              >
              {/* Main Card */}
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 h-full border border-gray-200/50 shadow-lg hover:shadow-2xl transition-all duration-500 group-hover:scale-105 group-hover:border-transparent overflow-hidden">
                
                {/* Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-3xl`}></div>
                
                {/* Animated Border */}
                <div className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${service.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}>
                  <div className="absolute inset-[2px] rounded-3xl bg-white/80 backdrop-blur-sm"></div>
                </div>

                <div className="relative z-10">
                  {/* Core Service Badge */}
                  {service.badge && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      {service.badge}
                    </div>
                  )}
                  
                  {/* Icon Container */}
                  <div className="flex items-center justify-between mb-6">
                    <div className={`p-4 rounded-2xl bg-gradient-to-r ${service.color} shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110`}>
                      <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {service.icon}
                      </svg>
                    </div>
                    
                    {/* Animated Arrow */}
                    <div className="opacity-0 group-hover:opacity-100 transform translate-x-4 group-hover:translate-x-0 transition-all duration-500">
                      <div className={`p-3 rounded-full bg-gradient-to-r ${service.color} text-white`}>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-gray-900 group-hover:to-gray-700 group-hover:bg-clip-text transition-all duration-300">
                    {service.title}
                  </h3>
                  
                  <p className="mt-4 text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                    {service.text}
                  </p>

                  {/* Features List */}
                  <div className="mt-6 space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="flex items-center text-gray-500 group-hover:text-gray-700 transition-colors duration-300"
                      >
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${service.color} mr-3 group-hover:scale-125 transition-transform duration-300`}></div>
                        <span className="text-sm font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Hover Effect Line */}
                  <div className={`absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r ${service.color} group-hover:w-full transition-all duration-1000 rounded-full`}></div>
                </div>

                {/* Floating Elements */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="flex space-x-1">
                    {[1, 2, 3].map((dot) => (
                      <div
                        key={dot}
                        className={`w-1 h-1 rounded-full bg-gradient-to-r ${service.color} animate-bounce`}
                        style={{ animationDelay: `${dot * 0.2}s` }}
                      ></div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Glow Effect */}
              <div className={`absolute -inset-1 bg-gradient-to-r ${service.color} rounded-3xl blur opacity-0 group-hover:opacity-20 transition-opacity duration-500 -z-10`}></div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`mt-16 text-center transition-all duration-1000 delay-1000 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <p className="text-gray-600 mb-6">Ready to transform your digital presence?</p>
          <button className="group relative overflow-hidden bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 py-4 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-indigo-500/30">
            <span className="relative z-10 flex items-center">
              Explore All Services
              <svg className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </span>
            <div className="absolute inset-0 -left-full group-hover:left-full w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-all duration-700"></div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Services;